<template>
  <div class="modal-overlay">
    <div class="modal-container" @click.stop ref="modalRef">
      <div class="modal-header">
        <div class="header-left">
          <h2 class="modal-title">{{ t('title') }}</h2>
          <div class="language-select-wrapper" @click.stop>
            <div
              class="custom-select"
              :class="{ 'is-open': dropdownOpen === 'language' }"
              @click="toggleDropdown('language')"
            >
              <span>{{ currentLang === 'zh' ? '中文' : 'English' }}</span>
              <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
            </div>
            <div v-if="dropdownOpen === 'language'" class="custom-dropdown">
              <div class="dropdown-item" :class="{ selected: currentLang === 'en' }" @click="setLanguage('en')">English</div>
              <div class="dropdown-item" :class="{ selected: currentLang === 'zh' }" @click="setLanguage('zh')">中文</div>
            </div>
          </div>
        </div>
        <button class="close-btn" @click="handleClose" type="button">
          <img :src="closeFillIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div class="modal-body">
        <div class="form-content">
          <div class="form-group">
            <label class="form-label required">{{ t('type') }}</label>
            <div class="custom-select-wrapper" :class="{ error: errors.type }" @click.stop @focusout="handleTypeFocusOut">
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'type' }"
                @click="toggleDropdown('type')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': !formData.type }">
                  {{ formData.type ? getLabel(formData.type, typeOptions) : t('ph_type') }}
                </span>
                <div class="select-trail">
                  <img v-if="errors.type" :src="alertIcon" alt="error" class="error-icon-select" />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="dropdownOpen === 'type'" class="custom-dropdown">
                <div
                  v-for="opt in typeOptions"
                  :key="opt.value"
                  class="dropdown-item"
                  :class="{ selected: formData.type === opt.value }"
                  @click="selectOption('type', opt.value)"
                >
                  {{ getLabel(opt.value, typeOptions) }}
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label required">{{ t('itemName') }}</label>
            <div class="input-wrapper" :class="{ error: errors.itemName || errors.itemNameLength || errors.itemNameLang }">
              <input
                :value="currentLang === 'zh' ? formData.itemNameZh : formData.itemName"
                type="text"
                class="form-input"
                :placeholder="t('ph_name')"
                maxlength="201"
                @input="handleItemNameInput"
                @blur="handleItemNameBlur"
              />
              <img
                v-if="errors.itemName || errors.itemNameLength || errors.itemNameLang"
                :src="alertIcon"
                alt="error"
                class="error-icon"
              />
            </div>
            <span v-if="errors.itemNameLength" class="error-text">Please keep it under 200 characters</span>
          </div>

          <div class="form-group">
            <label class="form-label required">{{ t('itemIcon') }}</label>
            <div class="upload-container" :class="{ error: errors.icon }">
              <div v-if="!previewUrl" class="upload-area" @click="triggerFileUpload">
                <img :src="photoIcon" alt="upload" class="upload-icon" />
                <span class="upload-text">{{ t('upload') }}</span>
              </div>
              <div v-else class="preview-area">
                <img :src="previewUrl" alt="preview" class="preview-image" />
                <div class="delete-overlay" @click="handleDeleteImage">
                  <img :src="deleteIcon" alt="delete" class="delete-icon" />
                  <span class="delete-text">{{ t('delete') }}</span>
                </div>
              </div>
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                class="file-input"
                @change="handleFileUpload"
              />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label required">{{ t('duration') }}</label>
            <div class="custom-select-wrapper" :class="{ error: errors.duration }" @click.stop @focusout="handleDurationFocusOut">
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'duration' }"
                @click="toggleDropdown('duration')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': !formData.duration }">
                  {{ formData.duration ? getDurationLabel(formData.duration) : t('ph_duration') }}
                </span>
                <div class="select-trail">
                  <img v-if="errors.duration" :src="alertIcon" alt="error" class="error-icon-select" />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="dropdownOpen === 'duration'" class="custom-dropdown">
                <div
                  v-for="option in durationOptions"
                  :key="option.value"
                  class="dropdown-item"
                  :class="{ selected: formData.duration === option.value }"
                  @click="selectOption('duration', option.value)"
                >
                  {{ option.label }}
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label required">{{ t('seasons') }}</label>
            <div class="custom-select-wrapper" :class="{ error: errors.seasons }" @click.stop @focusout="handleSeasonsFocusOut">
              <div
                class="custom-select custom-select--seasons"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'seasons' }"
                @click="toggleDropdown('seasons')"
              >
                <span
                  v-if="formData.seasons.length === 0"
                  class="custom-select-value placeholder-text"
                >
                  {{ t('ph_seasons') }}
                </span>
                <div v-else class="season-select-tags">
                  <span
                    v-for="option in selectedSeasonOptions"
                    :key="option.value"
                    class="season-tag"
                  >
                    {{ option.label }}
                  </span>
                </div>
                <div class="select-trail">
                  <img v-if="errors.seasons" :src="alertIcon" alt="error" class="error-icon-select" />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="dropdownOpen === 'seasons'" class="custom-dropdown checkbox-dropdown">
                <div class="dropdown-item" :class="{ selected: isAllSeasonsSelected }" @click.stop="toggleAllSeasons">
                  <div class="checkbox-wrapper">
                    <div class="checkbox" :class="{ checked: isAllSeasonsSelected }">
                      <svg v-if="isAllSeasonsSelected" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M2 6L5 9L10 3"
                          stroke="white"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <span class="checkbox-label all-label">{{ t('all') }}</span>
                  </div>
                </div>
                <div
                  v-for="option in seasonOptions"
                  :key="option.value"
                  class="dropdown-item"
                  :class="{ selected: formData.seasons.includes(option.value) }"
                  @click.stop="toggleSeason(option.value)"
                >
                  <div class="checkbox-wrapper">
                    <div
                      class="checkbox"
                      :class="{ checked: formData.seasons.includes(option.value) }"
                    >
                      <svg
                        v-if="formData.seasons.includes(option.value)"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2 6L5 9L10 3"
                          stroke="white"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <span class="checkbox-label">{{ option.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label required">{{ t('section') }}</label>
            <div class="custom-select-wrapper" :class="{ error: errors.section }" @click.stop @focusout="handleSectionFocusOut">
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'section' }"
                @click="toggleDropdown('section')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': !formData.section }">
                  {{
                    formData.section ? getLabel(formData.section, sectionOptions) : t('ph_section')
                  }}
                </span>
                <div class="select-trail">
                  <img v-if="errors.section" :src="alertIcon" alt="error" class="error-icon-select" />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="dropdownOpen === 'section'" class="custom-dropdown">
                <div
                  v-for="option in sectionOptions"
                  :key="option.value"
                  class="dropdown-item"
                  :class="{ selected: formData.section === option.value }"
                  @click="selectOption('section', option.value)"
                >
                  {{ getLabel(option.value, sectionOptions) }}
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label form-label--optional">{{ t('location') }}</label>
            <div ref="locationContainerRef" class="location-input" @click="focusLocationInput">
              <span
                v-for="(tag, index) in formData.locations"
                :key="`${tag}-${index}`"
                class="location-tag"
                :class="{ 'location-tag--focused': focusedTagIndex === index }"
                tabindex="-1"
                @click.stop="onTagClick(index)"
                @keydown="handleTagKeydown($event, index)"
              >
                {{ tag }}
              </span>
              <input
                ref="locationInputRef"
                v-model="locationDraft"
                class="location-input-field"
                type="text"
                :placeholder="formData.locations.length === 0 ? t('ph_location') : ''"
                @keydown="handleLocationKeydown"
                @blur="handleLocationBlur"
              />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label required">{{ t('photoRequired') }}</label>
            <div
              class="custom-select-wrapper"
              :class="{ error: errors.photoRequired }"
              @click.stop
              @focusout="handlePhotoRequiredFocusOut"
            >
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'photoRequired' }"
                @click="toggleDropdown('photoRequired')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': !formData.photoRequired }">
                  {{
                    formData.photoRequired
                      ? getLabel(formData.photoRequired, photoOptions)
                      : t('select')
                  }}
                </span>
                <div class="select-trail">
                  <img
                    v-if="errors.photoRequired"
                    :src="alertIcon"
                    alt="error"
                    class="error-icon-select"
                  />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="dropdownOpen === 'photoRequired'" class="custom-dropdown">
                <div
                  v-for="opt in photoOptions"
                  :key="opt.value"
                  class="dropdown-item"
                  :class="{ selected: formData.photoRequired === opt.value }"
                  @click="selectOption('photoRequired', opt.value)"
                >
                  {{ getLabel(opt.value, photoOptions) }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="formData.type === 'routine'" class="form-group">
            <label class="form-label required">{{ t('followUpNeeded') }}</label>
            <div
              class="custom-select-wrapper"
              :class="{ error: errors.followUpNeeded }"
              @click.stop
              @focusout="handleFollowUpFocusOut"
            >
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'followUpNeeded' }"
                @click="toggleDropdown('followUpNeeded')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': !formData.followUpNeeded }">
                  {{
                    formData.followUpNeeded
                      ? getLabel(formData.followUpNeeded, followUpOptions)
                      : t('select')
                  }}
                </span>
                <div class="select-trail">
                  <img
                    v-if="errors.followUpNeeded"
                    :src="alertIcon"
                    alt="error"
                    class="error-icon-select"
                  />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="dropdownOpen === 'followUpNeeded'" class="custom-dropdown">
                <div
                  v-for="opt in followUpOptions"
                  :key="opt.value"
                  class="dropdown-item"
                  :class="{ selected: formData.followUpNeeded === opt.value }"
                  @click="selectOption('followUpNeeded', opt.value)"
                >
                  {{ getLabel(opt.value, followUpOptions) }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="formData.type === 'routine' && formData.followUpNeeded === 'yes'" class="form-group">
            <label class="form-label required">{{ t('planIncludes') }}</label>
            <div class="custom-select-wrapper" :class="{ error: errors.planIncludesFollowUp }" @click.stop @focusout="handlePlanFocusOut">
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'planIncludesFollowUp' }"
                @click="toggleDropdown('planIncludesFollowUp')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': formData.planIncludesFollowUp.length === 0 }">
                  {{ getPlanLabel(formData.planIncludesFollowUp) }}
                </span>
                <div class="select-trail">
                  <img
                    v-if="errors.planIncludesFollowUp"
                    :src="alertIcon"
                    alt="error"
                    class="error-icon-select"
                  />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div
                v-if="dropdownOpen === 'planIncludesFollowUp'"
                class="custom-dropdown checkbox-dropdown"
              >
                <div class="dropdown-item" :class="{ selected: isAllPlansSelected }" @click.stop="toggleAllPlans">
                  <div class="checkbox-wrapper">
                    <div class="checkbox" :class="{ checked: isAllPlansSelected }">
                      <svg v-if="isAllPlansSelected" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M2 6L5 9L10 3"
                          stroke="white"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <span class="checkbox-label all-label">{{ t('all') }}</span>
                  </div>
                </div>
                <div
                  v-for="option in planOptions"
                  :key="option.value"
                  class="dropdown-item"
                  :class="{ selected: formData.planIncludesFollowUp.includes(option.value) }"
                  @click.stop="togglePlan(option.value)"
                >
                  <div class="checkbox-wrapper">
                    <div
                      class="checkbox"
                      :class="{ checked: formData.planIncludesFollowUp.includes(option.value) }"
                    >
                      <svg
                        v-if="formData.planIncludesFollowUp.includes(option.value)"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2 6L5 9L10 3"
                          stroke="white"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <span class="checkbox-label">{{ option.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label required">{{ t('frequency') }}</label>
            <div class="custom-select-wrapper" :class="{ error: errors.frequency }" @click.stop @focusout="handleFrequencyFocusOut">
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'frequency' }"
                @click="toggleDropdown('frequency')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': !formData.frequency }">
                  {{
                    formData.frequency
                      ? getLabel(formData.frequency, frequencyOptions)
                      : t('ph_frequency')
                  }}
                </span>
                <div class="select-trail">
                  <img v-if="errors.frequency" :src="alertIcon" alt="error" class="error-icon-select" />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="dropdownOpen === 'frequency'" class="custom-dropdown">
                <div
                  v-for="opt in frequencyOptions"
                  :key="opt.value"
                  class="dropdown-item"
                  :class="{ selected: formData.frequency === opt.value }"
                  @click="selectOption('frequency', opt.value)"
                >
                  {{ opt.label }}
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label required">{{ t('description') }}</label>
            <div class="textarea-wrapper" :class="{ error: errors.description || errors.descriptionLang }">
              <textarea
                :value="currentLang === 'zh' ? formData.descriptionZh : formData.description"
                class="form-textarea"
                :placeholder="t('ph_desc')"
                :maxlength="5000"
                @blur="handleDescriptionBlur"
                @input="handleDescriptionInput"
              ></textarea>
              <div class="char-count">
                {{
                  (currentLang === 'zh' ? formData.descriptionZh : formData.description).length || 0
                }}/5000
              </div>
              <img
                v-if="errors.description || errors.descriptionLang"
                :src="alertIcon"
                alt="error"
                class="error-icon-textarea"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="button-group">
        <button class="btn-cancel" @click="handleCancel" type="button">{{ t('cancel') }}</button>
        <button class="btn-submit" :disabled="submitting" @click="handleSubmit" type="button">
          {{ t('submit') }}
        </button>
      </div>
    </div>

    <div class="toast-area">
      <ToastComponent :visible="toastVisible" :type="toastType" :message="toastMessage" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed, onBeforeUnmount } from 'vue'
import {
  createChecklist,
  type ChecklistCreateParams,
  type ChecklistCreateResponse,
} from '@/api/checklist'
import request from '@/api/request'
import downarrowIcon from '@/assets/downarrow@2x.png'
import photoIcon from '@/assets/photo.png'
import alertIcon from '@/assets/alert.png'
import closeFillIcon from '@/assets/Fill 1@2x.png'
import deleteIcon from '@/assets/delete_white@2x.png'
import '@/styles/custom-select-trail.css'
import { FIELD_LIMITS } from '@/constants/fieldLimits'
import { validateFieldLimits, validateStringLength } from '@/utils/fieldLimitValidation'
import {
  validateChineseText,
  validateEnglishText,
} from '@/utils/textLanguageValidation'
import ToastComponent from '@/components/ToastComponent.vue'
import { TOAST_DURATION } from '@/constants/toast'
import { useLocationTagInput } from '@/utils/useLocationTagInput'
import '@/styles/checklist-location-input.css'

const checklistFieldLimits = FIELD_LIMITS.checklist

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
}>()

interface OptionItem {
  value: string
  label?: string
  labelEn?: string
  labelZh?: string
}

const messages = {
  en: {
    title: 'Create New Item',
    type: 'Type',
    itemName: 'Item name ',
    itemIcon: 'Item icon',
    duration: 'Duration',
    seasons: 'Seasons',
    section: 'Section',
    location: 'Location',
    photoRequired: 'Photo Required',
    followUpNeeded: 'Follow-up Needed (if issues found):',
    planIncludes: 'Plan includes follow-up care',
    frequency: 'Frequency',
    description: 'Description:',
    upload: 'Upload',
    cancel: 'Cancel',
    submit: 'Submit',
    select: 'Select',
    all: 'All',
    delete: 'Delete',
    ph_type: 'Select type',
    ph_name: 'Enter item name (max 200 characters)',
    ph_duration: 'Select duration',
    ph_seasons: 'Select seasons',
    ph_section: 'Select section',
    ph_location: 'Enter location',
    ph_plan: 'Select plan includes follow-up care',
    ph_frequency: 'Select frequency',
    ph_desc: 'Enter check description',
  },
  zh: {
    title: 'Create New Item',
    type: 'Type',
    itemName: 'Item name (中文)',
    itemIcon: 'Item icon',
    duration: 'Duration',
    seasons: 'Seasons',
    section: 'Section',
    location: 'Location',
    photoRequired: 'Photo Required',
    followUpNeeded: 'Follow-up Needed (if issues found):',
    planIncludes: 'Plan includes follow-up care',
    frequency: 'Frequency',
    description: 'Description (中文):',
    upload: 'Upload',
    cancel: 'Cancel',
    submit: 'Submit',
    select: 'Select',
    all: 'All',
    delete: 'Delete',
    ph_type: 'Select type',
    ph_name: 'Enter item name in Chinese (max 200 characters)',
    ph_duration: 'Select duration',
    ph_seasons: 'Select seasons',
    ph_section: 'Select section',
    ph_location: 'Enter location',
    ph_plan: 'Select plan includes follow-up care',
    ph_frequency: 'Select frequency',
    ph_desc: 'Enter check description in Chinese',
  },
}

const currentLang = ref<'en' | 'zh'>('en')
const fileInput = ref<HTMLInputElement | null>(null)
const dropdownOpen = ref<string | null>(null)
const toastVisible = ref(false)
const toastType = ref<'success' | 'warning' | 'error'>('success')
const toastMessage = ref('')
const submitting = ref(false)
const visible = ref(true)
let toastTimer: ReturnType<typeof setTimeout> | null = null
const previewUrl = ref<string>('')
const modalRef = ref<HTMLDivElement | null>(null)

const t = (key: keyof typeof messages.en): string => {
  return messages[currentLang.value][key]
}

interface FormData {
  type: string
  itemName: string
  itemNameZh: string
  icon: File | null
  duration: string
  seasons: string[]
  section: string
  /** 用户自定义 Location 标签（如 #Roof）；后续接后端 */
  locations: string[]
  photoRequired: string
  followUpNeeded: string
  planIncludesFollowUp: string[]
  frequency: string
  description: string
  descriptionZh: string
}

interface Errors {
  itemName?: boolean
  itemNameLength?: boolean
  itemNameLang?: boolean
  icon?: boolean
  type?: boolean
  duration?: boolean
  seasons?: boolean
  section?: boolean
  locations?: boolean
  photoRequired?: boolean
  followUpNeeded?: boolean
  planIncludesFollowUp?: boolean
  frequency?: boolean
  description?: boolean
  descriptionLang?: boolean
}

const formData = reactive<FormData>({
  type: '',
  itemName: '',
  itemNameZh: '',
  icon: null,
  duration: '',
  seasons: [],
  section: '',
  locations: [],
  photoRequired: 'yes',
  followUpNeeded: 'yes',
  planIncludesFollowUp: [],
  frequency: '',
  description: '',
  descriptionZh: '',
})

const errors = reactive<Errors>({})

const {
  locationDraft,
  locationInputRef,
  locationContainerRef,
  focusedTagIndex,
  focusLocationInput,
  commitLocationDraft,
  handleLocationKeydown,
  handleTagKeydown,
  handleLocationBlur,
  onTagClick,
} = useLocationTagInput(() => formData.locations)

const typeOptions = [
  { value: 'routine', labelEn: 'Routine', labelZh: '常规' },
  { value: 'baseline', labelEn: 'Baseline', labelZh: '基线' },
]

const followUpOptions = [
  { value: 'yes', labelEn: 'Yes', labelZh: '是' },
  { value: 'no', labelEn: 'No', labelZh: '否' },
]

const photoOptions = [
  { value: 'yes', labelEn: 'Yes', labelZh: 'Yes' },
  { value: 'no', labelEn: 'No', labelZh: 'No' },
]

const frequencyOptions = [
  { value: 'annually', label: 'Annually' },
  { value: 'semi-annually', label: 'Semi-annually' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'one-time', label: 'One-time' },
]

const durationOptions = [
  { value: '5', label: '5 mins' },
  { value: '10', label: '10 mins' },
  { value: '15', label: '15 mins' },
  { value: '20', label: '20 mins' },
  { value: '25', label: '25 mins' },
  { value: '30', label: '30 mins' },
  { value: '60', label: '1 Hour' },
  { value: '90', label: '1.5 Hours' },
  { value: '120', label: '2.0 Hours' },
  { value: '150', label: '2.5 Hours' },
  { value: '180', label: '3.0 Hours' },
]

const seasonOptions = [
  { value: 'spring', label: 'Spring' },
  { value: 'summer', label: 'Summer' },
  { value: 'fall', label: 'Fall' },
  { value: 'winter', label: 'Winter' },
]

const sectionOptions = [
  { value: 'roofing', labelEn: 'Roofing' },
  { value: 'exterior', labelEn: 'Exterior' },
  { value: 'structure', labelEn: 'Structure' },
  { value: 'electrical', labelEn: 'Electrical' },
  { value: 'heating', labelEn: 'Heating' },
  { value: 'cooling', labelEn: 'Cooling' },
  { value: 'insulation', labelEn: 'Insulation' },
  { value: 'plumbing', labelEn: 'Plumbing' },
  { value: 'interior', labelEn: 'Interior' },
]

const planOptions = [
  { value: 'essential', label: 'Essential Care' },
  { value: 'enhanced', label: 'Enhanced Care' },
  { value: 'premium', label: 'Premium Care' },
]

function getLabel(value: string, options: OptionItem[]): string {
  const opt = options.find((o) => o.value === value)
  if (!opt) return value
  return opt.labelEn || opt.label || value
}

function getDurationLabel(val: string): string {
  const opt = durationOptions.find((o) => o.value === val)
  return opt ? opt.label : t('ph_duration')
}

function getPlanLabel(vals: string[]): string {
  if (vals.length === 0) return t('ph_plan')
  if (vals.length === planOptions.length) return t('all')
  return vals.map((v) => planOptions.find((o) => o.value === v)?.label || '').join(', ')
}

const isAllSeasonsSelected = computed(
  () => formData.seasons.length === seasonOptions.length && seasonOptions.length > 0,
)

const selectedSeasonOptions = computed(() =>
  seasonOptions.filter((option) => formData.seasons.includes(option.value)),
)

const isAllPlansSelected = computed(
  () => formData.planIncludesFollowUp.length === planOptions.length && planOptions.length > 0,
)

function toggleSeason(value: string): void {
  const index = formData.seasons.indexOf(value)
  if (index > -1) {
    formData.seasons.splice(index, 1)
  } else {
    formData.seasons.push(value)
  }
  if (formData.seasons.length > 0) errors.seasons = false
}

function toggleAllSeasons(): void {
  if (isAllSeasonsSelected.value) {
    formData.seasons = []
  } else {
    formData.seasons = seasonOptions.map((o) => o.value)
  }
  if (formData.seasons.length > 0) errors.seasons = false
}

function togglePlan(value: string): void {
  const index = formData.planIncludesFollowUp.indexOf(value)
  if (index > -1) {
    formData.planIncludesFollowUp.splice(index, 1)
  } else {
    formData.planIncludesFollowUp.push(value)
  }
  if (formData.planIncludesFollowUp.length > 0) errors.planIncludesFollowUp = false
}

function toggleAllPlans(): void {
  if (isAllPlansSelected.value) {
    formData.planIncludesFollowUp = []
  } else {
    formData.planIncludesFollowUp = planOptions.map((o) => o.value)
  }
  if (formData.planIncludesFollowUp.length > 0) errors.planIncludesFollowUp = false
}

function validateDropdownField(fieldName: string): void {
  if (fieldName === 'type') errors.type = isEmpty(formData.type)
  else if (fieldName === 'duration') errors.duration = isEmpty(formData.duration)
  else if (fieldName === 'seasons') errors.seasons = formData.seasons.length === 0
  else if (fieldName === 'section') errors.section = isEmpty(formData.section)
  else if (fieldName === 'photoRequired') errors.photoRequired = isEmpty(formData.photoRequired)
  else if (fieldName === 'followUpNeeded') {
    if (formData.type === 'routine') errors.followUpNeeded = isEmpty(formData.followUpNeeded)
  }   else if (fieldName === 'planIncludesFollowUp') {
    if (formData.type === 'routine' && formData.followUpNeeded === 'yes') {
      errors.planIncludesFollowUp = formData.planIncludesFollowUp.length === 0
    }
  } else if (fieldName === 'frequency') errors.frequency = isEmpty(formData.frequency)
}

function toggleDropdown(fieldName: string): void {
  const wasOpen = dropdownOpen.value === fieldName
  dropdownOpen.value = wasOpen ? null : fieldName
  if (wasOpen) validateDropdownField(fieldName)
}

function selectOption(fieldName: string, value: string): void {
  ;(formData as Record<string, string | string[] | File | null>)[fieldName] = value
  dropdownOpen.value = null

  const errorMap: Partial<Record<string, keyof Errors>> = {
    type: 'type',
    duration: 'duration',
    section: 'section',
    photoRequired: 'photoRequired',
    followUpNeeded: 'followUpNeeded',
    frequency: 'frequency',
  }
  const errorKey = errorMap[fieldName]
  if (errorKey) errors[errorKey] = false

  if (fieldName === 'followUpNeeded' && value !== 'yes') {
    formData.planIncludesFollowUp = []
    errors.planIncludesFollowUp = false
  }
}

function isEmpty(value: string | undefined | null): boolean {
  return !String(value ?? '').trim()
}

function isFocusLeavingWrapper(event: FocusEvent): boolean {
  const wrapper = event.currentTarget as HTMLElement
  return !wrapper.contains(event.relatedTarget as Node)
}

function handleTypeFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.type = isEmpty(formData.type)
}

function getCurrentItemNameValue(): string {
  return currentLang.value === 'zh' ? formData.itemNameZh : formData.itemName
}

function validateCurrentItemNameLanguage(): void {
  if (currentLang.value === 'zh') {
    errors.itemNameLang =
      !formData.itemNameZh.trim() ||
      (formData.itemNameZh.trim().length > 0 && !validateChineseText(formData.itemNameZh))
  } else {
    errors.itemNameLang = formData.itemName.trim().length > 0 && !validateEnglishText(formData.itemName)
  }
}

function validateCurrentDescriptionLanguage(): void {
  if (currentLang.value === 'zh') {
    errors.descriptionLang =
      !formData.descriptionZh.trim() ||
      (formData.descriptionZh.trim().length > 0 && !validateChineseText(formData.descriptionZh))
  } else {
    errors.descriptionLang =
      formData.description.trim().length > 0 && !validateEnglishText(formData.description)
  }
}

function isEnglishTextFieldsIncomplete(): boolean {
  return (
    !formData.itemName.trim() ||
    !validateEnglishText(formData.itemName) ||
    !formData.description.trim() ||
    !validateEnglishText(formData.description)
  )
}

function validateSharedFields(): boolean {
  errors.type = !formData.type
  errors.icon = !formData.icon
  errors.duration = !formData.duration
  errors.seasons = formData.seasons.length === 0
  errors.section = !formData.section
  errors.photoRequired = !formData.photoRequired
  errors.followUpNeeded = formData.type === 'routine' && !formData.followUpNeeded
  errors.planIncludesFollowUp =
    formData.type === 'routine' &&
    formData.followUpNeeded === 'yes' &&
    formData.planIncludesFollowUp.length === 0
  errors.frequency = !formData.frequency
  return (
    !errors.type &&
    !errors.icon &&
    !errors.duration &&
    !errors.seasons &&
    !errors.section &&
    !errors.photoRequired &&
    !errors.followUpNeeded &&
    !errors.planIncludesFollowUp &&
    !errors.frequency
  )
}

function validateEnglishTextFields(): boolean {
  const itemNameMissing = !formData.itemName.trim()
  const descriptionMissing = !formData.description.trim()
  const itemNameLangError =
    formData.itemName.trim().length > 0 && !validateEnglishText(formData.itemName)
  const descriptionLangError =
    formData.description.trim().length > 0 && !validateEnglishText(formData.description)

  errors.itemName = itemNameMissing || itemNameLangError
  errors.description = descriptionMissing || descriptionLangError

  return !itemNameMissing && !descriptionMissing && !itemNameLangError && !descriptionLangError
}

function validateChineseTextFields(required = false): boolean {
  const itemNameZhMissing = !formData.itemNameZh.trim()
  const descriptionZhMissing = !formData.descriptionZh.trim()
  const itemNameZhLangError =
    formData.itemNameZh.trim().length > 0 && !validateChineseText(formData.itemNameZh)
  const descriptionZhLangError =
    formData.descriptionZh.trim().length > 0 && !validateChineseText(formData.descriptionZh)

  errors.itemNameLang = required
    ? itemNameZhMissing || itemNameZhLangError
    : itemNameZhLangError
  errors.descriptionLang = required
    ? descriptionZhMissing || descriptionZhLangError
    : descriptionZhLangError

  if (required) {
    return (
      !itemNameZhMissing &&
      !descriptionZhMissing &&
      !itemNameZhLangError &&
      !descriptionZhLangError
    )
  }
  return !itemNameZhLangError && !descriptionZhLangError
}

function getChineseValidationMessage(): string | null {
  if (!formData.itemNameZh.trim() || !validateChineseText(formData.itemNameZh)) {
    return 'Please enter the Chinese item name.'
  }
  if (!formData.descriptionZh.trim() || !validateChineseText(formData.descriptionZh)) {
    return 'Please enter the Chinese description.'
  }
  return null
}

function getEnglishValidationMessage(): string | null {
  if (!formData.itemName.trim() || !validateEnglishText(formData.itemName)) {
    return 'Please enter the English item name.'
  }
  if (!formData.description.trim() || !validateEnglishText(formData.description)) {
    return 'Please enter the English description.'
  }
  return null
}

function getLanguageValidationMessage(): string | null {
  if (currentLang.value === 'zh') {
    return getChineseValidationMessage() || getEnglishValidationMessage()
  }
  return getEnglishValidationMessage() || getChineseValidationMessage()
}

function handleItemNameBlur() {
  if (currentLang.value === 'zh') {
    errors.itemName = false
  } else {
    errors.itemName = !formData.itemName.trim()
  }
  validateCurrentItemNameLanguage()
}

function handleItemNameInput(event: Event): void {
  const target = event.target as HTMLInputElement
  if (currentLang.value === 'zh') {
    formData.itemNameZh = target.value
    if (target.value.trim() && validateChineseText(formData.itemNameZh)) {
      errors.itemNameLang = false
    }
  } else {
    formData.itemName = target.value
    if (target.value.trim()) {
      errors.itemName = false
    }
  }
  validateItemNameLength()
  validateCurrentItemNameLanguage()
}

function handleDurationFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.duration = isEmpty(formData.duration)
}

function handleSeasonsFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.seasons = formData.seasons.length === 0
}

function handleSectionFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.section = isEmpty(formData.section)
}

function handlePhotoRequiredFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.photoRequired = isEmpty(formData.photoRequired)
}

function handleFollowUpFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  if (formData.type !== 'routine') return
  errors.followUpNeeded = isEmpty(formData.followUpNeeded)
}

function handlePlanFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  if (formData.type !== 'routine' || formData.followUpNeeded !== 'yes') return
  errors.planIncludesFollowUp = formData.planIncludesFollowUp.length === 0
}

function handleFrequencyFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.frequency = isEmpty(formData.frequency)
}

function handleDescriptionBlur() {
  if (currentLang.value === 'zh') {
    errors.description = false
  } else {
    errors.description = !formData.description.trim()
  }
  validateCurrentDescriptionLanguage()
}

function handleDescriptionInput(event: Event): void {
  const target = event.target as HTMLTextAreaElement
  if (currentLang.value === 'zh') {
    formData.descriptionZh = target.value
    if (target.value.trim() && validateChineseText(formData.descriptionZh)) {
      errors.descriptionLang = false
    }
  } else {
    formData.description = target.value
    if (target.value.trim()) {
      errors.description = false
    }
  }
  validateCurrentDescriptionLanguage()
}

function setLanguage(lang: 'en' | 'zh'): void {
  currentLang.value = lang
  dropdownOpen.value = null
}

function closeDropdowns(event: MouseEvent) {
  const target = event.target as HTMLElement
  if (!target.closest('.custom-select-wrapper')) {
    if (dropdownOpen.value) {
      validateDropdownField(dropdownOpen.value)
      dropdownOpen.value = null
    }
  }
}

onMounted(() => {
  modalRef.value?.addEventListener('click', closeDropdowns)
})

// ✅ 组件销毁前清理定时器，防止内存泄漏
onBeforeUnmount(() => {
  if (toastTimer) {
    clearTimeout(toastTimer)
    toastTimer = null
  }
  modalRef.value?.removeEventListener('click', closeDropdowns)
})

function triggerFileUpload(): void {
  fileInput.value?.click()
}

function handleFileUpload(event: Event): void {
  const input = event.target as HTMLInputElement
  if (input.files && input.files[0]) {
    const file = input.files[0] as File
    formData.icon = file
    previewUrl.value = URL.createObjectURL(file)
    errors.icon = false
  }
}

function handleDeleteImage(): void {
  formData.icon = null
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
    previewUrl.value = ''
  }
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

// ✅ 修复Toast显示逻辑，确保显示时长正确
function showToastLocal(type: 'success' | 'warning' | 'error', message: string): void {
  if (toastTimer) {
    clearTimeout(toastTimer)
  }
  toastType.value = type
  toastMessage.value = message
  toastVisible.value = true

  toastTimer = setTimeout(() => {
    toastVisible.value = false
    toastTimer = null
  }, TOAST_DURATION.brief)
}

async function uploadIcon(): Promise<string> {
  if (!formData.icon) return ''
  const uploadFormData = new FormData()
  uploadFormData.append('category', 'checklist_icon')
  uploadFormData.append('file', formData.icon)
  const res = await request.post('/api/admin/upload', uploadFormData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  const data = res as { data?: { url?: string } }
  return data.data?.url || ''
}

function validateItemNameLength(): void {
  const currentValue = getCurrentItemNameValue()
  if (currentValue.length > checklistFieldLimits.itemName) {
    if (currentLang.value === 'zh') {
      formData.itemNameZh = currentValue.slice(0, checklistFieldLimits.itemName)
    } else {
      formData.itemName = currentValue.slice(0, checklistFieldLimits.itemName)
    }
    errors.itemNameLength = true
  } else {
    errors.itemNameLength = false
  }
}

function validateForm(): boolean {
  validateItemNameLength()

  const sharedValid = validateSharedFields()

  if (currentLang.value === 'zh') {
    const chineseValid = validateChineseTextFields(true)
    if (!chineseValid) {
      showToastLocal(
        'warning',
        getChineseValidationMessage() || 'Please enter the Chinese item name.',
      )
      return false
    }

    validateEnglishTextFields()
    if (isEnglishTextFieldsIncomplete()) {
      setLanguage('en')
      showToastLocal(
        'warning',
        getEnglishValidationMessage() || 'Please enter the English item name.',
      )
      return false
    }
  } else {
    const englishValid = validateEnglishTextFields()
    const chineseValid = validateChineseTextFields(false)
    if (!englishValid || !chineseValid) {
      showToastLocal(
        'warning',
        getLanguageValidationMessage() || 'Please complete all required fields.',
      )
      return false
    }
  }

  if (!sharedValid || errors.itemNameLength) {
    showToastLocal(
      'warning',
      currentLang.value === 'zh' ? '请完成所有必填项' : 'Please complete all required fields.',
    )
    return false
  }

  const limitErr = validateFieldLimits([
    { value: formData.itemName, max: checklistFieldLimits.itemName, label: 'Item name' },
    { value: formData.itemNameZh, max: checklistFieldLimits.itemName, label: 'Item name' },
    { value: formData.description, max: checklistFieldLimits.description, label: 'Description' },
    { value: formData.descriptionZh, max: checklistFieldLimits.description, label: 'Description' },
  ])
  if (limitErr) {
    showToastLocal('warning', limitErr)
    return false
  }

  return true
}

function handleSubmit(): void {
  // 未按 Enter 的草稿一并打成标签再提交
  commitLocationDraft()

  if (!validateForm()) {
    return
  }

  submitting.value = true
  uploadIcon()
    .then((iconUrl) => {
      if (!iconUrl) {
        errors.icon = true
        showToastLocal(
          'warning',
          currentLang.value === 'zh' ? '请完成所有必填项' : 'Please complete all required fields.',
        )
        return Promise.reject(new Error('Icon required'))
      }
      const iconErr = validateStringLength(
        iconUrl,
        checklistFieldLimits.iconUrl,
        'Icon URL',
      )
      if (iconErr) {
        showToastLocal('warning', iconErr)
        return Promise.reject(new Error(iconErr))
      }
      const submitData: ChecklistCreateParams = {
        item_name: formData.itemName.trim(),
        item_name_zh: formData.itemNameZh.trim(),
        type: formData.type,
        duration_minutes: parseInt(formData.duration),
        seasons: formData.seasons,
        section: formData.section,
        frequency: formData.frequency,
        description: formData.description.trim(),
        description_zh: formData.descriptionZh.trim(),
        icon_url: iconUrl,
        sort: 20,
        status: 1,
      }
      submitData.locations = [...formData.locations]

      // 所有类型都提交 photo_required 字段
      submitData.photo_required = formData.photoRequired === 'yes' ? 1 : 0

      if (formData.type === 'routine') {
        submitData.follow_up_needed = formData.followUpNeeded === 'yes' ? 1 : 0
        if (formData.followUpNeeded === 'yes' && formData.planIncludesFollowUp.length > 0) {
          submitData.plan_includes_follow_up_care = formData.planIncludesFollowUp.map(
            (plan: string) => {
              if (plan === 'essential') return 'essential_care'
              if (plan === 'enhanced') return 'enhanced_care'
              return 'premium_care'
            },
          )
        }
        submitData.plan_values = {
          essential_care: 49,
          enhanced_care: 99,
          premium_care: 199,
        }
      }

      return createChecklist(submitData)
    })
    .then((res: ChecklistCreateResponse) => {
      if (res.code === 200) {
        emit('saved')
        emit('close')
      } else {
        showToastLocal('error', res.msg || 'Failed to create')
      }
    })
    .catch((err: unknown) => {
      console.error('请求错误:', err)
      showToastLocal(
        'error',
        currentLang.value === 'zh'
          ? '请求失败，请稍后重试'
          : 'Request failed, please try again later',
      )
    })
    .finally(() => {
      submitting.value = false
    })
}

function handleCancel(): void {
  visible.value = false
  setTimeout(() => emit('close'), 300)
}

function handleClose(): void {
  emit('close')
}
</script>

<style scoped>
/* ✅ Toast全局定位，z-index高于所有弹窗 */
.toast-area {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 999999; /* 比原来高一位，确保在最顶层 */
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.modal-container {
  background: #fff;
  border-radius: 12px;
  width: 90%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-width: 530px;
  max-height: 90vh;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 24px 0;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  min-height: 24px;
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
  padding: 0;
  white-space: nowrap;
}

.language-select-wrapper {
  position: relative;
  width: 110px;
  height: 24px;
  flex-shrink: 0;
}

.language-select-wrapper .custom-select {
  height: 24px;
  border-radius: 16px;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: center;
  padding: 0 16px 0 12px;
  box-sizing: border-box;
}

.close-btn {
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
}



.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.modal-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 16px 24px 0;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  margin: 0;
  color: #000000;
}

.form-label.required::before {
  content: '*';
  color: #e54848;
  margin-right: 4px;
}

/* 非必填：占位与红 * 同宽，文字与其它标签对齐 */
.form-label--optional::before {
  content: '*';
  color: transparent;
  margin-right: 4px;
}

.form-input,
.custom-select {
  height: 32px;
  line-height: 1;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  width: 100%;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-wrapper.error {
  border: 1px solid #ff4d4f;
  border-radius: 100px;
  background: #fffafa;
}

.form-input {
  padding: 0 12px;
  border: 1px solid #e0e0e0;
  border-radius: 100px;
  outline: none;
  transition: border-color 0.2s;
  background: #fff;
  color: #000000;
}

.form-input::placeholder {
  color: #CCCCCC;
}

.form-input:hover {
  border-color: #e0e0e0;
}

.form-input:focus {
  border-color: #666666;
}

.input-wrapper.error .form-input {
  border: none;
  height: 32px;
}

.error-icon {
  position: absolute;
  right: 8px;
  width: 16px;
  height: 16px;
  object-fit: contain;
  pointer-events: none;
}

.error-text {
  display: block;
  margin-top: 6px;
  font-size: 0.85rem;
  color: #ff4d4f;
}

.upload-container {
  position: relative;
}

.upload-container.error .upload-area {
  border-color: #ff4d4f;
  background: #fffafa;
}

.upload-area {
  width: 96px;
  height: 96px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: border-color 0.2s;
  background: #fff;
}

.upload-icon {
  width: 32px;
  height: 32px;
}

.upload-text {
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.preview-area {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.delete-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
}

.preview-area:hover .delete-overlay {
  opacity: 1;
}

.delete-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.delete-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #ffffff;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.file-input {
  display: none;
}

.custom-select-wrapper {
  position: relative;
}

.custom-select-wrapper.error {
  border: 1px solid #ff4d4f;
  border-radius: 100px;
  background: #fffafa;
}

.custom-select {
  padding: 0 12px;
  border: 1px solid #e0e0e0;
  border-radius: 100px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s;
  user-select: none;
  display: flex;
  align-items: center;
  color: #000000;
}

.custom-select:hover,
.custom-select:focus {
  border-color: #e0e0e0;
  outline: none;
}

.custom-select.is-open {
  border-color: #666666;
}

.custom-select-wrapper.error .custom-select {
  border: none;
  height: 32px;
}

.placeholder-text {
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #CCCCCC;
  line-height: 20px;
}

.select-arrow {
  transition: transform 0.2s ease;
}

.custom-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox-dropdown {
  max-height: 300px;
  overflow-y: auto;
}

.dropdown-item {
  padding: 8px 16px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: #000000;
  cursor: pointer;
  transition: background 0.2s;
}

.dropdown-item:hover {
  background: #f5f5f5;
}

.custom-dropdown .dropdown-item.selected {
  font-weight: 600;
}

.dropdown-item.selected .checkbox-label {
  font-weight: 600;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.checkbox {
  width: 18px;
  height: 18px;
  border: 2px solid #e0e0e0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
}

.checkbox.checked {
  background-color: #000000;
  border-color: #000000;
}

.checkbox-label {
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.textarea-wrapper {
  position: relative;
}

.textarea-wrapper.error {
  border: 1px solid #ff4d4f;
  border-radius: 12px;
  background: #fffafa;
}

.textarea-wrapper.error .form-textarea {
  border: none;
}

.error-icon-textarea {
  position: absolute;
  right: 12px;
  top: 12px;
  width: 16px;
  height: 16px;
  object-fit: contain;
  pointer-events: none;
  z-index: 2;
}

.form-textarea {
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  outline: none;
  resize: vertical;
  font-size: 14px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  transition: border-color 0.2s;
}

.form-textarea:hover {
  border-color: #e0e0e0;
}

.form-textarea:focus {
  border-color: #666666;
}

.form-textarea::placeholder {
  color: #CCCCCC;
}

.char-count {
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 12px;
  color: #999;
}

.button-group {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 24px;
  background: #fff;
  flex-shrink: 0;
}

.btn-cancel,
.btn-submit {
  width: 80px;
  height: 32px;
  padding: 0;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.btn-cancel {
  background: #fff;
  border: 1px solid #000000;
  color: #000000;
}

.btn-cancel:hover {
  background: #000000;
  color: #ffffff;
}

.btn-submit {
  background: #000000;
  color: #fff;
}

.btn-submit:hover {
  background: #484848;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }

  .modal-body {
    padding: 16px 16px 16px;
  }
}
</style>
